/**
 * Data Context
 * Manages unified storage layer (Firestore + localStorage fallback)
 * Now works with active baby instead of user
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useBaby } from './BabyContext';
import type { BabyData, FeedEntry, SleepEntry, DiaperEntry, GrowthEntry } from '../types';
import {
  loadBabyDataFromFirestore,
  addFeedToBaby,
  addSleepToBaby,
  addDiaperToBaby,
  addGrowthToBaby,
  deleteEntryFromBaby,
  updateEntryInBaby,
} from '@/lib/firestore-baby';
import { saveDataToLocalStorage, loadDataFromLocalStorage } from '@/lib/firestore';

interface DataContextType {
  data: BabyData;
  loading: boolean;
  addFeed: (feed: Omit<FeedEntry, 'id'>) => Promise<FeedEntry>;
  addSleep: (sleep: Omit<SleepEntry, 'id'>) => Promise<SleepEntry>;
  addDiaper: (diaper: Omit<DiaperEntry, 'id'>) => Promise<DiaperEntry>;
  addGrowth: (growth: Omit<GrowthEntry, 'id'>) => Promise<GrowthEntry>;
  deleteEntry: (type: 'feeds' | 'sleeps' | 'diapers' | 'growth', id: string) => Promise<void>;
  updateEntry: (type: 'feeds' | 'sleeps' | 'diapers' | 'growth', id: string, updates: any) => Promise<void>;
  refreshData: () => Promise<void>;
  babyName: string;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

function getEmptyData(): BabyData {
  return {
    feeds: [],
    sleeps: [],
    diapers: [],
    growth: [],
    version: '1.0.0',
    createdAt: new Date(),
    lastModified: new Date(),
  };
}

export function DataProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { activeBaby, loading: babyLoading } = useBaby();
  const [data, setData] = useState<BabyData>(getEmptyData());
  const [loading, setLoading] = useState(true);

  // Load data when active baby changes
  useEffect(() => {
    if (user && activeBaby) {
      loadBabyData();
    } else {
      setData(getEmptyData());
      setLoading(false);
    }
  }, [user, activeBaby]);

  const loadBabyData = async () => {
    if (!user || !activeBaby) return;

    // Show cached data instantly while Firestore loads in background
    const cached = loadDataFromLocalStorage();
    if (cached.feeds.length > 0 || cached.sleeps.length > 0 || cached.diapers.length > 0) {
      setData(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const firestoreData = await loadBabyDataFromFirestore(activeBaby.id);
      setData(firestoreData);
      saveDataToLocalStorage(firestoreData);
    } catch (error) {
      console.error('[DataContext] Error loading baby data:', error);
      if (!cached.feeds.length && !cached.sleeps.length) setData(getEmptyData());
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    if (user && activeBaby) {
      await loadBabyData();
    }
  };

  const addFeed = async (feed: Omit<FeedEntry, 'id'>): Promise<FeedEntry> => {
    if (!user || !activeBaby) {
      // Fallback to localStorage
      const { addFeed: addFeedLocal } = await import('@/lib/storage');
      return addFeedLocal(feed);
    }

    const newFeed = await addFeedToBaby(activeBaby.id, feed);
    setData((prev) => ({
      ...prev,
      feeds: [newFeed, ...prev.feeds],
    }));
    return newFeed;
  };

  const addSleep = async (sleep: Omit<SleepEntry, 'id'>): Promise<SleepEntry> => {
    if (!user || !activeBaby) {
      const { addSleep: addSleepLocal } = await import('@/lib/storage');
      return addSleepLocal(sleep);
    }

    const newSleep = await addSleepToBaby(activeBaby.id, sleep);
    setData((prev) => ({
      ...prev,
      sleeps: [newSleep, ...prev.sleeps],
    }));
    return newSleep;
  };

  const addDiaper = async (diaper: Omit<DiaperEntry, 'id'>): Promise<DiaperEntry> => {
    if (!user || !activeBaby) {
      const { addDiaper: addDiaperLocal } = await import('@/lib/storage');
      return addDiaperLocal(diaper);
    }

    const newDiaper = await addDiaperToBaby(activeBaby.id, diaper);
    setData((prev) => ({
      ...prev,
      diapers: [newDiaper, ...prev.diapers],
    }));
    return newDiaper;
  };

  const addGrowth = async (growth: Omit<GrowthEntry, 'id'>): Promise<GrowthEntry> => {
    if (!user || !activeBaby) {
      const { addGrowth: addGrowthLocal } = await import('@/lib/storage');
      return addGrowthLocal(growth);
    }

    const newGrowth = await addGrowthToBaby(activeBaby.id, growth);
    setData((prev) => ({
      ...prev,
      growth: [newGrowth, ...prev.growth],
    }));
    return newGrowth;
  };

  const deleteEntry = async (type: 'feeds' | 'sleeps' | 'diapers' | 'growth', id: string): Promise<void> => {
    if (!user || !activeBaby) {
      const { deleteEntry: deleteEntryLocal } = await import('@/lib/storage');
      const typeMap = { feeds: 'feed', sleeps: 'sleep', diapers: 'diaper', growth: 'growth' } as const;
      return deleteEntryLocal(typeMap[type], id);
    }

    await deleteEntryFromBaby(activeBaby.id, type, id);
    setData((prev) => ({
      ...prev,
      [type]: prev[type].filter((item: any) => item.id !== id),
    }));
  };

  const updateEntry = async (type: 'feeds' | 'sleeps' | 'diapers' | 'growth', id: string, updates: any): Promise<void> => {
    if (!user || !activeBaby) {
      const storage = await import('@/lib/storage');
      const updateMap = {
        feeds: storage.updateFeed,
        sleeps: storage.updateSleep,
        diapers: storage.updateDiaper,
        growth: storage.updateGrowth,
      };
      return updateMap[type](id, updates);
    }

    await updateEntryInBaby(activeBaby.id, type, id, updates);
    setData((prev) => ({
      ...prev,
      [type]: prev[type].map((item: any) => (item.id === id ? { ...item, ...updates } : item)),
    }));
  };

  const value = {
    data,
    loading: loading || babyLoading,
    addFeed,
    addSleep,
    addDiaper,
    addGrowth,
    deleteEntry,
    updateEntry,
    refreshData,
    babyName: activeBaby?.name || 'Baby',
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}
